import Alert, { type AlertProps } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import type { ContextualSegmentsDrawerState } from '@openx/types/targeting/advancedTargeting.ts';
import type { ContextualSegmentsDataPayload } from '@openx/types/targeting/contextualSegments.ts';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables.ts';
import { ComparisonType, Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { convertContextualToCustom, prepareContextualSegments } from '../../ContextualSegments/helpers.ts';
import { DimensionChip } from '../../shared/DimensionChip/DimensionChip.tsx';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems.tsx';
import type { TargetingItemsProps } from '../../types.ts';
import { SegmentIdsItems } from './SegmentIdsItems/SegmentIdsItems.tsx';

const StyledInfoAlert = (props: AlertProps) => (
  <Alert severity="info" sx={{ marginTop: 3, maxWidth: 'max-content' }} {...props} />
);

export interface Props extends TargetingItemsProps<ContextualSegmentsDrawerState> {
  onAdd?: (contextual: CustomVariablesDataPayload[]) => void;
  /** Not used by V1; accepted so V1 and V2 items share one interface. */
  accountUid?: string;
  /** Show the empty state; defaults to `!readonly`. */
  showEmptyMessage?: boolean;
}

// Contextual segments have no operator of their own; all selected segments must match
const contextualSegmentsOperator = Criteria.ALL;

export const ContextualSegmentsItems = ({
  targetingParams,
  readonly = true,
  onChange = noop,
  onAdd = noop,
  showEmptyMessage = !readonly,
}: Props) => {
  const { t } = useTranslation();
  const ctxSegments = useMemo(
    () => prepareContextualSegments(structuredClone(targetingParams.contextual)),
    [targetingParams.contextual]
  );

  const handleChange = useCallback(
    (values: ContextualSegmentsDataPayload[]) => {
      onChange(prevParams => ({
        ...prevParams,
        contextual: convertContextualToCustom(values),
      }));
    },
    [onChange]
  );

  const upsertContextualSegment = useCallback(
    (contextualSegment: ContextualSegmentsDataPayload) => {
      const variableIndex = ctxSegments.findIndex(ctxSegment => ctxSegment.attr === contextualSegment.attr);

      if (variableIndex !== -1) {
        ctxSegments.splice(variableIndex, 1);
      }

      const values = convertContextualToCustom([...ctxSegments, contextualSegment]);

      onAdd(values.length ? values : [{ attr: contextualSegment.attr, op: ComparisonType.INTERSECTS, val: '' }]);
    },
    [onAdd, ctxSegments]
  );

  const handleRemove = useCallback(
    (item: ContextualSegmentsDataPayload) => {
      handleChange(
        ctxSegments.reduce((acc, variable) => {
          if (variable.attr !== item.attr) {
            acc.push({ ...variable, attr: variable.attr });
          }

          return acc;
        }, [] as ContextualSegmentsDataPayload[])
      );
    },
    [ctxSegments, handleChange]
  );

  const emptySectionComponent = useMemo(
    () =>
      showEmptyMessage && (
        <Grid data-test="empty-section">
          <StyledInfoAlert>{t('You will be able to add the segments IDs after adding provider name.')}</StyledInfoAlert>
        </Grid>
      ),
    [showEmptyMessage, t]
  );

  const ContextualSegmentsList = useMemo(() => {
    return ctxSegments.length ? (
      <Grid data-test={'section-items-contextual-segments'}>
        {ctxSegments.map((item, index) => (
          <Fragment key={item.attr}>
            <SelectedItems
              filtersTitle={t('Provider name')}
              hideTitle={index !== 0}
              isAddIcon={true}
              selectedItems={[item]}
              readonly={readonly}
              handleChange={() => handleRemove(item)}
              getItemLabel={item => item.attr}
              dataTest="contextual-segments-provider"
            />
            <Box
              sx={{
                marginLeft: 3.75,
              }}
            >
              <SegmentIdsItems
                readonly={readonly}
                item={item}
                targetingDimension={contextualSegmentsOperator}
                onChange={upsertContextualSegment}
              />
              {index < ctxSegments.length - 1 && (
                <DimensionChip isInsideOption={true} targetingDimension={contextualSegmentsOperator} />
              )}
            </Box>
          </Fragment>
        ))}
      </Grid>
    ) : null;
  }, [t, handleRemove, readonly, ctxSegments, upsertContextualSegment]);

  const segmentsCount = ctxSegments.reduce((acc, item) => {
    if (item?.excludes?.length) {
      acc += item.excludes.split(',').length;
    }
    if (item?.includes?.length) {
      acc += item.includes.split(',').length;
    }

    return acc;
  }, 0);
  return ctxSegments?.length ? (
    <Box data-test="sub-section">
      <FiltersTitle
        onClear={() => handleChange([])}
        title={t('Contextual Segments [ {count} ]', {
          count: segmentsCount,
        })}
        readonly={readonly}
      />
      {ContextualSegmentsList}
    </Box>
  ) : (
    emptySectionComponent
  );
};
